/**
 * PlayerFinance - the single authority on money.
 *
 * Every cent that moves goes through `credit` or `debit`, which append to the
 * transaction ledger. The invariant the tests enforce is:
 *
 *     cash === startingCash + sum(all transaction amounts)
 *
 * If any system mutates `finance.cash` directly that invariant breaks, which is
 * exactly the bug we want a test to catch rather than a player.
 */
import type { GameState, TransactionCategory } from '../state/GameState.js';
import type { Cents } from '../core/money.js';
import type { EventBus } from '../core/events.js';
import type { IdGenerator } from '../core/ids.js';
import { BALANCE } from '../data/balance.js';
import { getEquipment, EMPLOYEE_KIT, VEHICLES_BY_ID, getHousing } from '../data/catalog.js';

export class FinanceSystem {
  constructor(
    private readonly bus: EventBus,
    private readonly ids: IdGenerator,
  ) {}

  private record(
    state: GameState,
    amount: Cents,
    category: TransactionCategory,
    label: string,
  ): void {
    state.finance.cash += amount;
    state.finance.transactions.push({
      id: this.ids.next('tx'),
      day: state.time.day,
      minute: state.time.minuteOfDay,
      amount,
      category,
      label,
      balanceAfter: state.finance.cash,
    });
    // The ledger is unbounded over a long game; keep the tail that the UI shows
    // plus enough history to stay useful, and fold the rest into the opening
    // balance implicitly (the invariant test uses a fresh, unpruned game).
    if (state.finance.transactions.length > 400) {
      state.finance.transactions.splice(0, state.finance.transactions.length - 300);
    }
    this.bus.emit({
      type: 'money.changed',
      delta: amount,
      balanceCash: state.finance.cash,
      reason: label,
    });
  }

  /** Money in. */
  credit(state: GameState, amount: Cents, category: TransactionCategory, label: string): void {
    if (amount <= 0) return;
    this.record(state, amount, category, label);
    state.finance.todayEarned += amount;
    state.finance.lifetimeEarned += amount;
  }

  /**
   * Money out. Returns false and changes nothing if the player cannot afford it,
   * so callers can present the purchase as refused rather than silently going
   * negative.
   */
  debit(state: GameState, amount: Cents, category: TransactionCategory, label: string): boolean {
    if (amount <= 0) return true;
    if (state.finance.cash < amount) return false;
    this.record(state, -amount, category, label);
    state.finance.todaySpent += amount;
    state.finance.lifetimeSpent += amount;
    return true;
  }

  /**
   * Charge something that cannot simply be declined, such as rent. Whatever
   * cannot be paid in cash becomes debt.
   */
  charge(state: GameState, amount: Cents, category: TransactionCategory, label: string): boolean {
    if (amount <= 0) return true;
    const payable = Math.min(state.finance.cash, amount);
    if (payable > 0) {
      this.record(state, -payable, category, label);
      state.finance.todaySpent += payable;
      state.finance.lifetimeSpent += payable;
    }
    const shortfall = amount - payable;
    if (shortfall > 0) {
      state.finance.debt += shortfall;
      this.bus.emit({ type: 'debt.taken', amount: shortfall, source: label });
      return false;
    }
    return true;
  }

  deposit(state: GameState, amount: Cents): boolean {
    if (amount <= 0 || state.finance.cash < amount) return false;
    this.record(state, -amount, 'sale', 'Bank deposit');
    state.finance.bank += amount;
    return true;
  }

  withdraw(state: GameState, amount: Cents): boolean {
    if (amount <= 0 || state.finance.bank < amount) return false;
    state.finance.bank -= amount;
    this.record(state, amount, 'sale', 'Bank withdrawal');
    return true;
  }

  takeLoan(state: GameState, amount: Cents): boolean {
    const headroom = BALANCE.debt.maxLoan - state.finance.debt;
    if (amount <= 0 || amount > headroom) return false;
    if (state.player.reputation < BALANCE.debt.minReputationForLoan) return false;
    state.finance.debt += amount;
    this.record(state, amount, 'loan', 'Bank loan');
    this.bus.emit({ type: 'debt.taken', amount, source: 'Bank loan' });
    return true;
  }

  repayDebt(state: GameState, amount: Cents): boolean {
    const owed = Math.min(amount, state.finance.debt);
    if (owed <= 0 || state.finance.cash < owed) return false;
    this.record(state, -owed, 'repayment', 'Debt repayment');
    state.finance.debt -= owed;
    this.bus.emit({ type: 'debt.repaid', amount: owed, remaining: state.finance.debt });
    return true;
  }

  /**
   * Net worth = liquid assets + resale value of what you own - what you owe.
   * This is the number the $10,000 progression gate is measured against, so it
   * deliberately values assets at resale, not at purchase price.
   */
  netWorth(state: GameState): Cents {
    let total = state.finance.cash + state.finance.bank;

    for (const id of state.business.equipment) {
      const def = getEquipment(id);
      total += Math.round(def.price * def.resale);
    }
    total += state.business.crewKits * Math.round(EMPLOYEE_KIT.price * EMPLOYEE_KIT.resale);

    if (state.player.vehicleId) {
      const v = VEHICLES_BY_ID.get(state.player.vehicleId);
      if (v) total += Math.round(v.price * v.resale);
    }

    // A housing deposit is refundable, so it is an asset rather than a sunk cost.
    total += getHousing(state.player.housingId).deposit;

    total -= state.finance.debt;
    return total;
  }
}
